
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Building2,
  Plane,
  FileText,
  UploadCloud,
  X,
  Loader2,
  Play,
  Plus,
  Lock as LockIcon,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import PlanGate from '../../../../../components/gating/PlanGate';

const VoucherGeneratorCard = ({
  leftCardRef,
  isSideBySide,
  hasAnyRightTab,
  hotelFiles,
  setHotelFiles,
  travelFiles,
  setTravelFiles,
  itineraryFiles,
  setItineraryFiles,
  isHotelDragging,
  setIsHotelDragging,
  isTravelDragging,
  setIsTravelDragging,
  isItineraryDragging,
  setIsItineraryDragging,
  hotelInputRef,
  travelInputRef,
  itineraryInputRef,
  effectiveHotelFiles,
  effectiveTravelFiles,
  effectiveItineraryFiles,
  createDropHandler,
  handleFileInputChange,
  handleGenerate,
  isTourActive,
  onStartTour,
  isVoucherLocked,
  analysisState
}) => {
  const navigate = useNavigate();

  return (
    <div
      ref={leftCardRef}
      data-tour="create"
      className={`${isSideBySide ? 'flex-1 min-w-0' : 'w-full'
        } bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-6 border border-slate-100 shadow-sm flex flex-col hover:shadow-md transition-all duration-300 overflow-hidden`}
    >
      <div>
        {/* Header with Title & Badge */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-1">
          <div className="flex items-center justify-between w-full sm:w-auto gap-2">
            <h1 className="text-base sm:text-xl md:text-2xl font-normal text-slate-800 tracking-tight">
              Voucher Generator
            </h1>
            <span className="sm:hidden inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap shrink-0">
              <Sparkles size={11} className="text-primary shrink-0" />
              Trrip Magic
            </span>
          </div>
          <p className="hidden sm:block text-xs sm:text-sm text-slate-500 mt-1 font-normal leading-relaxed">
            Drop hotel confirmations, flight tickets, or itineraries. Trrip automatically generates your branded voucher.
          </p>
          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap shrink-0 ml-auto">
            <Sparkles size={11} className="text-primary shrink-0" />
            Trrip Magic
          </span>
        </div>

        {/* 3 Upload Drop Boxes */}
        <div
          data-tour="upload-boxes"
          className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mt-3 mb-3 items-stretch"
        >
          {/* Box 1: Hotel Booking (Required) */}
          <div
            className={`h-full ${hasAnyRightTab && isSideBySide
                ? 'min-h-[145px] sm:min-h-[155px]'
                : 'min-h-[155px] sm:min-h-[165px]'
              } rounded-2xl p-3 sm:p-3.5 border-2 border-dashed transition-all duration-200 flex flex-col justify-between ${isHotelDragging
                ? 'border-primary bg-primary/5 scale-[1.01]'
                : hotelFiles.length > 0
                  ? 'border-primary/40 bg-teal-50/40'
                  : 'border-slate-200 hover:border-primary/50 bg-slate-50/50 hover:bg-white'
              }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsHotelDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsHotelDragging(false);
            }}
            onDrop={createDropHandler(setHotelFiles, hotelFiles, [travelFiles, itineraryFiles], setIsHotelDragging)}
          >
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Building2 size={13} />
                  </div>
                  <span className="text-xs font-normal text-slate-800 whitespace-nowrap">Hotel</span>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0 whitespace-nowrap">
                  Required
                </span>
              </div>

              <p className="text-[11px] text-slate-500 leading-tight">
                PDF, voucher or hotel screenshot
              </p>
            </div>

            {/* Uploaded Files or Drop Area */}
            <div className="mt-3">
              {effectiveHotelFiles.length > 0 ? (
                <div className="space-y-1 max-h-[64px] overflow-y-auto pr-1">
                  {effectiveHotelFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-1 bg-white px-2 py-1 rounded-lg border border-primary/20 text-[11px] font-medium text-slate-700 shadow-2xs"
                    >
                      <span className="truncate max-w-[120px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHotelFiles((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="p-0.5 hover:text-red-500 rounded text-slate-400 shrink-0"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (isVoucherLocked) {
                      navigate('/subscription');
                      toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                    } else {
                      hotelInputRef.current?.click();
                    }
                  }}
                  className="w-full py-2 px-2.5 bg-white hover:bg-primary/5 border border-slate-200 hover:border-primary/40 rounded-xl flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-primary transition-colors whitespace-nowrap shadow-2xs"
                >
                  <UploadCloud size={14} className="text-primary" />
                  <span>
                    <span className="sm:hidden">Add</span>
                    <span className="hidden sm:inline">Upload Hotel</span>
                  </span>
                </button>
              )}
              <input
                type="file"
                multiple
                accept=".pdf,image/*"
                ref={hotelInputRef}
                onChange={handleFileInputChange(setHotelFiles, hotelFiles, [travelFiles, itineraryFiles])}
                className="hidden"
              />
            </div>
          </div>

          {/* Box 2: Flight / Train Tickets (Optional) */}
          <div
            className={`h-full ${hasAnyRightTab && isSideBySide
                ? 'min-h-[145px] sm:min-h-[155px]'
                : 'min-h-[155px] sm:min-h-[165px]'
              } rounded-2xl p-3 sm:p-3.5 border-2 border-dashed transition-all duration-200 flex flex-col justify-between ${isTravelDragging
                ? 'border-primary bg-primary/5 scale-[1.01]'
                : travelFiles.length > 0
                  ? 'border-primary/40 bg-teal-50/40'
                  : 'border-slate-200 hover:border-primary/50 bg-slate-50/50 hover:bg-white'
              }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsTravelDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsTravelDragging(false);
            }}
            onDrop={createDropHandler(setTravelFiles, travelFiles, [hotelFiles, itineraryFiles], setIsTravelDragging)}
          >
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Plane size={13} />
                  </div>
                  <span className="text-xs font-normal text-slate-800 whitespace-nowrap">Tickets</span>
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0 whitespace-nowrap">
                  Optional
                </span>
              </div>

              <p className="text-[11px] text-slate-500 leading-tight">
                Flight, train or bus passes
              </p>
            </div>

            {/* Uploaded Files or Drop Area */}
            <div className="mt-3">
              {effectiveTravelFiles.length > 0 ? (
                <div className="space-y-1 max-h-[64px] overflow-y-auto pr-1">
                  {effectiveTravelFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-1 bg-white px-2 py-1 rounded-lg border border-primary/20 text-[11px] font-medium text-slate-700 shadow-2xs"
                    >
                      <span className="truncate max-w-[120px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTravelFiles((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="p-0.5 hover:text-red-500 rounded text-slate-400 shrink-0"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (isVoucherLocked) {
                      navigate('/subscription');
                      toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                    } else {
                      travelInputRef.current?.click();
                    }
                  }}
                  className="w-full py-2 px-2.5 bg-white hover:bg-primary/5 border border-slate-200 hover:border-primary/40 rounded-xl flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-primary transition-colors whitespace-nowrap shadow-2xs"
                >
                  <UploadCloud size={14} className="text-primary" />
                  <span>
                    <span className="sm:hidden">Add</span>
                    <span className="hidden sm:inline">Upload Tickets</span>
                  </span>
                </button>
              )}
              <input
                type="file"
                multiple
                accept=".pdf,image/*"
                ref={travelInputRef}
                onChange={handleFileInputChange(setTravelFiles, travelFiles, [hotelFiles, itineraryFiles])}
                className="hidden"
              />
            </div>
          </div>

          {/* Box 3: Itinerary Importer (Optional) */}
          <div
            className={`h-full col-span-1 ${hasAnyRightTab && isSideBySide
                ? 'min-h-[145px] sm:min-h-[155px]'
                : 'min-h-[155px] sm:min-h-[165px]'
              } rounded-2xl p-3 sm:p-3.5 border-2 border-dashed transition-all duration-200 flex flex-col justify-between ${isItineraryDragging
                ? 'border-primary bg-primary/5 scale-[1.01]'
                : itineraryFiles.length > 0
                  ? 'border-primary/40 bg-teal-50/40'
                  : 'border-slate-200 hover:border-primary/50 bg-slate-50/50 hover:bg-white'
              }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsItineraryDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsItineraryDragging(false);
            }}
            onDrop={createDropHandler(setItineraryFiles, itineraryFiles, [hotelFiles, travelFiles], setIsItineraryDragging)}
          >
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <FileText size={13} />
                  </div>
                  <span className="text-xs font-normal text-slate-800 whitespace-nowrap">Itinerary</span>
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0 whitespace-nowrap">
                  Optional
                </span>
              </div>

              <p className="text-[11px] text-slate-500 leading-tight">
                Day-by-day plan or text
              </p>
            </div>

            {/* Uploaded Files or Drop Area */}
            <div className="mt-3">
              {effectiveItineraryFiles.length > 0 ? (
                <div className="space-y-1 max-h-[64px] overflow-y-auto pr-1">
                  {effectiveItineraryFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-1 bg-white px-2 py-1 rounded-lg border border-primary/20 text-[11px] font-medium text-slate-700 shadow-2xs"
                    >
                      <span className="truncate max-w-[200px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setItineraryFiles((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="p-0.5 hover:text-red-500 rounded text-slate-400 shrink-0"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (isVoucherLocked) {
                      navigate('/subscription');
                      toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                    } else {
                      itineraryInputRef.current?.click();
                    }
                  }}
                  className="w-full py-2 px-2.5 bg-white hover:bg-primary/5 border border-slate-200 hover:border-primary/40 rounded-xl flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-primary transition-colors whitespace-nowrap shadow-2xs"
                >
                  <UploadCloud size={14} className="text-primary" />
                  <span>
                    <span className="sm:hidden">Add</span>
                    <span className="hidden sm:inline">Upload Itinerary</span>
                  </span>
                </button>
              )}
              <input
                type="file"
                multiple
                accept=".pdf,image/*"
                ref={itineraryInputRef}
                onChange={handleFileInputChange(setItineraryFiles, itineraryFiles, [hotelFiles, travelFiles])}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar: Generate CTA & Manual Entry */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-2">
          {isVoucherLocked ? (
            <PlanGate
              featureKey="voucher-creation"
              featureName="Voucher Creation"
              showLockIcon={false}
              tooltipText="Subscription required to create vouchers."
            >
              {({ isLocked, requiredTier, planDetails }) => (
                <button
                  type="button"
                  onClick={() => {
                    navigate('/subscription');
                    toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
                  }}
                  className="w-full md:w-auto px-6 py-2.5 bg-[#067978] text-white rounded-xl text-sm font-semibold hover:bg-[#056665] shadow-lg shadow-[#067978]/20 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Plus size={18} />
                  <span>Create Voucher</span>
                  <LockIcon size={14} className="ml-1 text-white/90 shrink-0" />
                </button>
              )}
            </PlanGate>
          ) : (
            <button
              type="button"
              data-tour="generate-btn"
              onClick={handleGenerate}
              disabled={(!isTourActive && hotelFiles.length === 0) || analysisState.isAnalyzing}
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-[#056564] text-white text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-xl shadow-sm shadow-primary/20 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap"
            >
              {analysisState.isAnalyzing ? (
                <>
                  <Loader2 size={15} className="animate-spin text-white" />
                  Processing documents...
                </>
              ) : (
                <>
                  <Sparkles size={15} className="text-white" />
                  Generate Voucher
                </>
              )}
            </button>
          )}

          <button
            type="button"
            data-tour="tour-btn"
            onClick={() => onStartTour && onStartTour()}
            className="inline-flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-200 transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
          >
            <Play className="w-3.5 h-3.5 text-primary fill-primary/20 stroke-[2.5]" />
            Quick Tour
          </button>
        </div>

        {/* Quick Link to Manual creation */}
        <button
          type="button"
          onClick={() => {
            if (isVoucherLocked) {
              navigate('/subscription');
              toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
            } else {
              navigate('/create-voucher', { state: { activeTab: 'manual' } });
            }
          }}
          className="text-xs font-semibold text-slate-500 hover:text-primary transition-colors flex items-center gap-1 py-1 whitespace-nowrap cursor-pointer"
        >
          Or enter details manually
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};

export default VoucherGeneratorCard;
